import styled, { css } from 'styled-components';

import { Link, zIndex, Text } from '@comicrelief/component-library';

import { SecondaryNavMenuWrapper, ColumnWrapper } from './SecondaryNavMenu.style';

const transitionDuration = 0.2;

const NavLinkClass = styled(Link)`
  display: inline-block;
  border: 0;
  padding: 20px 25px;
  line-height: 1rem;
  height: auto;
  font-weight: 600;
  width: 100%;
  color: ${({ theme }) => theme.color('black')};
  transition: color ${transitionDuration}s ease;
  text-decoration: none;
  outline-offset: -3px;

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    outline-offset: 0;
  }

  :hover,
  :focus,
  :focus-within {
    border: 0;
  }

  :hover,
  :focus {
    color: ${({ theme }) => theme.color('red')};
    background-color: ${({ theme }) => theme.color('grey_extra_light')};
    font-weight: 600;
  }

  :focus-within {
    // Reset the colour of a parent...
    background-color: ${({ theme }) => theme.color('white')};

    // ...unless it's also being hovered
    &:hover {
      background-color: ${({ theme }) => theme.color('grey_extra_light')};
    }
  }
`;

const NavLink = styled(NavLinkClass)`
  font-family: ${({ theme }) => theme.fontFamilies(theme.font.regular)};

  ${({ isExpanded, theme }) => (isExpanded && css`
    &,
    &:hover,
    &:focus-within,
    &:focus {
      color: ${theme.color('red')};
    }
  `)}

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    height: auto;

    :hover, :focus-within, :focus {
      + ${SecondaryNavMenuWrapper} {
      display: flex;
      opacity: 1;
    }}

    ${({ hasSubMenu }) => (hasSubMenu && css`
      padding: 10px 16px 10px 0;
    `)}
  }
`;

const DesktopNavLink = styled(NavLink)`
  &,
  &:hover,
  &:focus,
  &:focus-within {
    background-color: ${({ theme }) => theme.color('white')};
  }
`;

const StyledNavItem = styled.li`
  ${zIndex('medium')};
  position: static;
  font-weight: 700;
  border-top: ${({ $hideContainer, theme }) => ($hideContainer ? 'none' : `1px solid ${theme.color('grey_medium')}`)};

  &:first-of-type {
    border-top: none;

    /* First link rounded corners to match menu */
    > a {
      border-radius: 16px 16px 0 0;
    }
  }

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    border-top: none;

    &:first-of-type > a {
      border-radius: 0;
    }
  }

  li {
    span {
      border-bottom: none;
      padding-bottom: 0;
      line-height: 1rem;

      @media ${({ theme }) => theme.breakpoints2026('L')} {
        line-height: 1.375rem;
      }
    }

    :hover,
    :focus,
    :focus-within {
      span {
        border-bottom: none;
        padding-bottom: 0;
      }
    }
  }

  // Chevron icon:
  span > a > span {
    opacity: 1;
    transition: opacity 0.3s ease-out;
  }

  :hover,
  :focus,
  :focus-within {
    li {
      span {
        border-bottom: none;
        padding-bottom: 0;
      }
    }

    // Chevron icon colour:
    span > a,
    > a {
      color: ${({ theme }) => theme.color('red')};
      > span {
        opacity: 1;
        img {
          filter: invert(0.5) sepia(1) saturate(100) hue-rotate(20deg);
        }
      }
    }
  }


  @media ${({ theme }) => theme.breakpoints2026('L')} {

    // Chevron icon rotation - desktop only:
    :hover,
    :focus,
    :focus-within {
      span > a > span {
        transform: translateY(-50%) rotate(-180deg);
      }
    }
    height: 100%;
    display: flex;
    align-items: center;
    border-bottom: none;
    position: static;

    :hover,
    :focus,
    :focus-within {
      > ${SecondaryNavMenuWrapper}, :focus-within > ${SecondaryNavMenuWrapper} {
        visibility: visible;
        opacity: 1;
        display: grid;
        transform: scaleY(1);
        transition: opacity 0.4s linear, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), visibility 0s linear 0s;

        ${ColumnWrapper} {
          opacity: 1;
          transform: translateY(0);
        }
      }
    }

    // Base state for span with pseudo-element border at bottom of header
    > span {
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: -14px;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: ${({ theme }) => theme.color('red')};
        transform: scaleX(0);
        transform-origin: center;
        transition: transform 0.3s ease-out;
      }
    }

    :hover,
    :focus,
    :focus-within {
      background-color: transparent;
      ${zIndex('high')};

      > span::after {
        transform: scaleX(1);
        transition: transform 0.15s ease-out;
      }
    }

    // Hide underline when dropdown menu is hovered, but keep primary link red
    &:has(${SecondaryNavMenuWrapper}:hover) > span::after {
      transform: scaleX(0);
    }
  }
`;

const ChevronWrapper = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 30px;
  width: 8px;

  img {
    width: 100%;
    display: block;
    height: auto;
    @media ${({ theme }) => theme.breakpoints2026('L')} {
      transform: rotate(90deg);
      filter: brightness(0);
    }
  }

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    width: 7px;
  }
`;

const StyledText = styled(Text)`
  @media ${({ theme }) => theme.breakpoints2026('L')} {
    font-size: 1rem;
  }
`;

export {
  StyledNavItem,
  NavLink,
  DesktopNavLink,
  ChevronWrapper,
  StyledText
};
